import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Stack } from 'expo-router';

import { BASE_STACK_SCREEN_OPTIONS } from '@/constants/screenOptions';
import { useColors } from '@/hooks/useColors';
import { useSecureRouteAccess } from '@/hooks/useSecureRouteAccess';

export type TabStackName = 'directory' | 'inbox' | 'inquiry' | 'community';

/**
 * The stack a tab's screens live on: the tab root, and every screen opened from it.
 *
 * Screens opened from a tab are pushed here rather than onto the secure layout's stack, so
 * they stay inside the tab, under its tab bar, the way iOS 26 apps behave. That is what lets
 * the header morph. iOS morphs bar buttons only between screens on one navigation bar, and
 * each tab's stack has its own, so a tab root's buttons can morph into the next screen's only
 * when both are on this stack.
 *
 * The screens shared by every tab live in one array group, which expo-router copies into each
 * tab. A push resolves to the copy in the tab the reader is already in. Chat is the exception,
 * kept to the Inbox tab alone: a copy per tab would run a live conversation twice.
 *
 * Every declaration here is what a guard or an option attaches to, as in the secure layout,
 * and the guards come from the same hook so a screen answers the same way on either stack.
 * The root is declared first because the first screen is the stack's initial route. Modals and
 * the screens a reader is parked on stay on the secure layout's stack, which covers the tabs.
 *
 * The status bar color repeats what the secure layout declares for the tab group, because the
 * innermost stack's screen is the one the native layer reads it from. Web draws each header
 * inside its page and switches pages without a transition, as the tab group's own stack does.
 */
export function TabStack({ tab }: { tab: TabStackName }) {
  const { colors, statusBarStyle } = useColors();

  const { mayUseVerifiedRoutes, mayUseFellowsCorner } = useSecureRouteAccess();

  return (
    <Stack
      screenOptions={{
        ...BASE_STACK_SCREEN_OPTIONS,
        statusBarBackgroundColor: colors.background[0],
        statusBarStyle,
        ...(isWeb ? { animation: 'none' } : null),
      }}>
      <Stack.Screen name={`${tab}/index`} />

      <Stack.Protected guard={mayUseVerifiedRoutes}>
        <Stack.Screen name="profile/[id]/index" />

        {/*
         * Old links to your own profile's edit screens. Each one redirects to its screen in Account,
         * where your profile and its edit screens share a stack, or to the profile for anyone else.
         */}
        <Stack.Screen name="profile/[id]/professional" />

        <Stack.Screen name="profile/[id]/about" />

        <Stack.Screen name="profile/[id]/work-history" />

        <Stack.Screen name="profile/[id]/education" />

        <Stack.Screen name="profile/[id]/disclosures" />
      </Stack.Protected>

      <Stack.Screen name="directory/inquiry/[id]" />

      <Stack.Screen
        name="directory/inquiry/success"
        options={{
          statusBarBackgroundColor: colors.primary[500],
          statusBarStyle: 'light',
          contentStyle: { backgroundColor: colors.primary[500] },
        }}
      />

      <Stack.Screen name="inquiry/new" />

      <Stack.Screen name="community/[id]" />

      <Stack.Screen name="content-library/[id]" />

      <Stack.Screen name="medical-on-demand/inquiries/[teamId]" />

      <Stack.Protected guard={mayUseFellowsCorner}>
        <Stack.Screen name="fellows/index" />
      </Stack.Protected>

      {tab === 'inbox' ? (
        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen name="inbox/chat/[id]" />

          <Stack.Screen name="inbox/compose" />
        </Stack.Protected>
      ) : null}

      {/*
       * Held to verified readers even though it sits under `/community`, which is otherwise
       * browsable while NPI verification is pending: the search requires an NPI server-side,
       * so reaching the screen early would only reach a refusal. Deliberately not gated on the
       * `library_chat` flag. The flag only decides whether the Inquiry Library shows the
       * entry point, so a shared link opens the screen for anyone the backend lets in.
       */}
      {tab === 'community' ? (
        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen name="community/ask" />

          <Stack.Screen
            name="community/ask-result"
            options={{ headerShown: true, statusBarTranslucent: true }}
          />
        </Stack.Protected>
      ) : null}
    </Stack>
  );
}
